<template>
  <div class="map-wrap">
    <GmapMap
        ref="map"
        :center="center"
        :zoom="8"
        :options="mapOptions"
        :map-type-id="mapTypeId"
        :style="{width: '100%', height: height}"
    >
      <GmapMarker
          v-for="(stop, index) in mappable"
          :key="'stop-' + index"
          :position="{lat: stop.lat, lng: stop.lng}"
          :icon="StopIcon(stop)"
          :label="{text: String(stop.label), color: 'white', fontWeight: 'bold'}"
          :title="stop.title || ''"
          @click="onMarkerClick(stop)"
      />
      <GmapInfoWindow
          v-if="infoStop && infoStop.ledger"
          :position="{lat: infoStop.lat, lng: infoStop.lng}"
          :opened="true"
          @closeclick="infoStop = null"
      >
        <div class="map-info">
          <div class="map-info-title">{{ infoStop.name || infoStop.title }}</div>
          <div class="map-info-links">
            <a @click="emitDrill(infoStop, 'in')"><b class="in">+{{ infoStop.ledger.in }}</b> {{ $t('fleetMaps.ledger_in') }}</a>
            <a @click="emitDrill(infoStop, 'out')"><b class="out">−{{ infoStop.ledger.out }}</b> {{ $t('fleetMaps.ledger_out') }}</a>
            <a @click="emitDrill(infoStop, 'on_board_leaving')"><b>{{ infoStop.ledger.leaving }}</b> {{ $t('fleetMaps.ledger_on_board') }}</a>
          </div>
          <div v-if="infoStop.ledger.offRoute || infoStop.ledger.offRouteLoaded" class="map-info-offroute">
            <a @click="emitDrill(infoStop, 'off_route')">
              <b>+{{ infoStop.ledger.offRouteLoaded || infoStop.ledger.offRoute }}</b> {{ $t('fleetMaps.off_route') }}
              <template v-if="infoStop.ledger.offRoute">{{ $t('fleetMaps.off_route_on_board', {n: infoStop.ledger.offRoute}) }}</template>
            </a>
          </div>
        </div>
      </GmapInfoWindow>
      <GmapMarker
          v-for="(point, index) in mappableCheckins"
          :key="'checkin-' + index"
          :position="{lat: point.lat, lng: point.lng}"
          :icon="checkinIcon"
          :title="point.title || ''"
      />
      <GmapPolyline
          v-if="displayPath.length > 1"
          :path="displayPath"
          :options="{strokeColor: '#195BFF', strokeOpacity: 0.85, strokeWeight: 4}"
      />
      <GmapMarker
          v-if="truckPos"
          :position="truckPos"
          :icon="TruckIcon()"
          :label="truckLabel"
          :title="truckTitle"
          :z-index="10"
          @click="truckInfoOpen = !truckInfoOpen"
      />
      <GmapInfoWindow
          v-if="truckPos && truckInfoOpen"
          :position="truckPos"
          :opened="true"
          :options="truckInfoOptions"
          @closeclick="truckInfoOpen = false"
      >
        <div class="truck-info">
          <div class="ti-head">
            <span class="ti-plate">{{ truck.plate || '—' }}</span>
            <span v-if="truck.direction" class="ti-dir"
                  :class="truck.direction === 'return' ? 'dir-return' : 'dir-outbound'">
              {{ truck.direction === 'return' ? $t('fleetMaps.return_leg') : $t('fleetMaps.outbound') }}
            </span>
            <span class="ti-live" :class="{stale: truck.stale}">
              {{ truck.stale ? $t('fleetMaps.stale') : $t('fleetMaps.live') }}
            </span>
          </div>
          <div v-if="truck.subtitle" class="ti-meta">{{ truck.subtitle }}</div>
          <div v-if="truck.stats && truck.stats.length" class="ti-lines">
            <span v-for="(stat, index) in truck.stats" :key="'ts-' + index">
              <b :style="stat.color ? {color: stat.color} : null">{{ stat.value }}</b> {{ stat.label }}
            </span>
          </div>
          <div v-if="truck.note" class="ti-note">{{ truck.note }}</div>
          <div v-if="truck.seenText" class="ti-seen">{{ $t('fleetMaps.last_seen', {time: truck.seenText}) }}</div>
        </div>
      </GmapInfoWindow>
    </GmapMap>
    <button class="map-mode-btn" :title="$t('fleetMaps.change_map_view')" @click="CycleMapMode">
      <i :class="currentMapMode.icon"></i> {{ currentMapMode.label }}
    </button>
  </div>
</template>

<script>
import {
  fleetPinIcon, fleetDotIcon, fleetTruckIcon, fleetTruckLabel, fleetRoadPath, FLEET_MAP_MODES,
} from '@/Mixins/Fleet/FleetHelpers';

/**
 * Display-only stops map (trip detail / anywhere a route needs drawing).
 * Same controls as the route builder: zoom/scale/fullscreen + the single
 * cycling map-mode button. Auto-fits to the given pins.
 *
 * props.stops:    [{lat, lng, type: 'branch'|'distribution_center', label, title, isStart}]
 * props.checkins: [{lat, lng, title}] — driver GPS check-in stamps (green dots)
 * props.truck:    the LIVE truck (US2), same marker as the dispatch map, or null.
 *                 {lat, lng, heading_deg, stale, plate, title} to draw it, plus
 *                 optional bubble content shown when the marker is tapped:
 *                 {direction, subtitle, stats: [{label, value, color}], note,
 *                 seenText}. The caller owns the wording — this component knows
 *                 where the truck is, not what its numbers mean.
 */
export default {
  name: 'FleetStopsMap',
  props: {
    stops: {type: Array, default: () => []},
    checkins: {type: Array, default: () => []},
    truck: {type: Object, default: null},
    height: {type: String, default: '420px'},
  },
  data: () => ({
    center: {lat: 18.1189434, lng: 102.290218},
    mapTypeId: 'roadmap',
    mapModes: FLEET_MAP_MODES,
    mapOptions: {
      zoomControl: true,
      scaleControl: true,
      fullscreenControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    },
    startIcon: fleetPinIcon('#2dcc70'),
    branchIcon: fleetPinIcon('#195BFF'),
    dcIcon: fleetPinIcon('#e8a10f'),
    mineIcon: fleetPinIcon('crimson'),
    checkinIcon: fleetDotIcon('#2dcc70'),
    roadPath: null,     // Directions-service road geometry (null = straight fallback)
    roadPathKey: '',    // stops fingerprint the roadPath was computed for
    infoStop: null,     // marker whose ledger info window is open
    truckInfoOpen: false,
    // The bubble tracks the marker, so it must not shove the map around every
    // time the truck glides a few metres.
    truckInfoOptions: {pixelOffset: {width: 0, height: -18}, disableAutoPan: true},
    // Coordinates the view is already framed to. Callers rebuild the `stops`
    // array on every poll (a fresh computed = a new identity), so without this
    // the watcher re-fitted the map every few seconds and stole back any zoom
    // the operator had set — which is exactly what makes a moving truck
    // impossible to watch. Only a genuine change of coordinates re-fits.
    fittedKey: '',
    // Live-truck glide, same reasoning as the dispatch map: a fix lands every
    // few seconds and setting the position outright makes the truck teleport,
    // which reads as a broken map rather than as a truck driving.
    truckAnim: null,    // the position actually DRAWN
    truckTween: null,   // {from, to, start} in flight
    truckRaf: null,
    truckIcons: {},     // colour+heading+plate => icon, so the marker never flickers
    lastHeading: null,  // a stationary fix reports none — keep the last real one
    animDurationMs: 1200,
    animMaxGlideKm: 5,  // beyond this it is a jump, not a drive: snap
  }),
  computed: {
    currentMapMode() {
      return this.mapModes.find((mode) => mode.id === this.mapTypeId) || this.mapModes[0];
    },
    mappable() {
      // Nudge repeat-visit markers that share coordinates (return-leg loops)
      // so both pins stay separately visible + clickable.
      const seen = {};
      return this.stops
          .filter((stop) => Number(stop.lat) && Number(stop.lng))
          .map((stop) => {
            let lat = Number(stop.lat);
            let lng = Number(stop.lng);
            const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
            const n = seen[key] || 0;
            seen[key] = n + 1;
            if (n > 0) {
              lat += 0.0006 * n;
              lng += 0.0006 * n;
            }
            return {...stop, lat, lng};
          });
    },
    mappableCheckins() {
      return this.checkins
          .filter((point) => Number(point.lat) && Number(point.lng))
          .map((point) => ({...point, lat: Number(point.lat), lng: Number(point.lng)}));
    },
    path() {
      return this.mappable.map((stop) => ({lat: stop.lat, lng: stop.lng}));
    },
    // Road-following line when Directions resolved it; straight line otherwise.
    displayPath() {
      return (this.roadPath && this.roadPathKey === JSON.stringify(this.path))
          ? this.roadPath : this.path;
    },
    // The truck only exists once it has reported real coordinates.
    hasTruck() {
      return !!(this.truck && Number(this.truck.lat) && Number(this.truck.lng));
    },
    // Where the truck is DRAWN — the tweened position mid-glide, the reported
    // one otherwise. Every NUMBER shown elsewhere stays the reported one.
    truckPos() {
      if (!this.hasTruck) return null;
      return this.truckAnim || {lat: Number(this.truck.lat), lng: Number(this.truck.lng)};
    },
    truckLabel() {
      return this.hasTruck ? fleetTruckLabel(this.truck.plate) : null;
    },
    truckTitle() {
      return this.hasTruck ? (this.truck.title || this.truck.plate || '') : '';
    },
  },
  watch: {
    stops: {
      handler() {
        this.FitToStops();
        this.RefreshRoadPath();
      },
    },
    truck: {
      handler() {
        this.RetargetTruck();
      },
    },
  },
  methods: {
    RefreshRoadPath() {
      const key = JSON.stringify(this.path);
      if (key === this.roadPathKey && this.roadPath) return;
      this.roadPathKey = key;
      this.roadPath = null;
      if (this.path.length < 2) return;
      // Wait for the maps API before asking Directions for the road geometry.
      const request = () => fleetRoadPath(this.path).then((road) => {
        if (this.roadPathKey === key) this.roadPath = road;
      }).catch(() => {
      });
      if (this.$refs.map && this.$refs.map.$mapPromise) {
        this.$refs.map.$mapPromise.then(request).catch(() => {
        });
      } else {
        request();
      }
    },
    CycleMapMode() {
      const index = this.mapModes.findIndex((mode) => mode.id === this.mapTypeId);
      this.mapTypeId = this.mapModes[(index + 1) % this.mapModes.length].id;
    },
    StopIcon(stop) {
      if (stop.isMine) return this.mineIcon; // the viewer's own facility
      if (stop.isStart) return this.startIcon;
      return stop.type === 'distribution_center' ? this.dcIcon : this.branchIcon;
    },
    onMarkerClick(stop) {
      this.center = {lat: stop.lat, lng: stop.lng};
      // Open the ledger info window only when this pin carries movement data.
      this.infoStop = stop.ledger ? stop : null;
    },
    emitDrill(stop, direction) {
      this.$emit('drill', {tripStopId: stop.tripStopId, direction});
      this.infoStop = null;
    },
    // Red while reporting, grey once contact drops — same language as the
    // dispatch map, so the two read identically. A METHOD, not a computed, so
    // it can memoise: handing Google a freshly built icon object on every
    // render makes the marker flicker. 5-degree heading buckets are finer than
    // the eye reads and cap the cache at ~72 entries per colour.
    TruckIcon() {
      if (!this.hasTruck) return null;
      const color = this.truck.stale ? '#9aa0a6' : '#e53935';
      const heading = this.CurrentHeading();
      const bucket = (heading === null) ? 'none' : Math.round(heading / 5) * 5;
      const plate = this.truck.plate || '';
      const key = `${color}:${bucket}:${plate}`;
      if (!this.truckIcons[key]) {
        this.truckIcons[key] = fleetTruckIcon(color, bucket === 'none' ? null : bucket, plate);
      }
      return this.truckIcons[key];
    },
    // The heading to draw: the reported one, or the last real one we saw. A
    // stationary fix often reports no heading, and no heading is not north.
    // PURE — `lastHeading` is written in RetargetTruck, never from a computed.
    CurrentHeading() {
      const raw = this.ReportedHeading();
      return raw === null ? this.lastHeading : raw;
    },
    ReportedHeading() {
      if (!this.truck || this.truck.heading_deg === null || this.truck.heading_deg === undefined) {
        return null;
      }
      const raw = Number(this.truck.heading_deg);
      return isNaN(raw) ? null : raw;
    },
    // Start (or retarget) the glide toward the newest fix. Retargeting
    // mid-glide is deliberate: a fix landing while the marker is still moving
    // continues from where it actually is instead of stuttering backwards.
    RetargetTruck() {
      if (!this.hasTruck) {
        this.truckAnim = null;
        this.truckTween = null;
        // A bubble pinned to a truck that is no longer on the map is orphaned.
        this.truckInfoOpen = false;
        return;
      }
      const reported = this.ReportedHeading();
      if (reported !== null) this.lastHeading = reported;
      const to = {lat: Number(this.truck.lat), lng: Number(this.truck.lng)};
      const from = this.truckAnim;
      if (!from) {
        this.truckAnim = to;         // first sight — nothing to glide from
        return;
      }
      if (from.lat === to.lat && from.lng === to.lng) {
        this.truckTween = null;      // parked
        return;
      }
      // Rough km — only used to choose glide-vs-snap, so a flat approximation
      // beats a haversine here.
      const dLat = (to.lat - from.lat) * 111;
      const dLng = (to.lng - from.lng) * 111 * Math.cos(to.lat * Math.PI / 180);
      if (Math.sqrt((dLat * dLat) + (dLng * dLng)) > this.animMaxGlideKm) {
        this.truckAnim = to;         // a jump, not a drive
        this.truckTween = null;
        return;
      }
      this.truckTween = {from: {lat: from.lat, lng: from.lng}, to, start: this.Now()};
      this.RunTruckAnimation();
    },
    Now() {
      return (window.performance && window.performance.now)
          ? window.performance.now() : Date.now();
    },
    RunTruckAnimation() {
      if (this.truckRaf) return;     // a loop is already running
      const step = () => {
        const tw = this.truckTween;
        if (!tw) {
          this.truckRaf = null;
          return;
        }
        const p = Math.min(1, (this.Now() - tw.start) / this.animDurationMs);
        // easeInOutQuad — a truck accelerates and settles.
        const e = p < 0.5 ? (2 * p * p) : (1 - (Math.pow((-2 * p) + 2, 2) / 2));
        this.truckAnim = {
          lat: tw.from.lat + ((tw.to.lat - tw.from.lat) * e),
          lng: tw.from.lng + ((tw.to.lng - tw.from.lng) * e),
        };
        if (p >= 1) {
          this.truckTween = null;
          this.truckRaf = null;
          return;
        }
        this.truckRaf = window.requestAnimationFrame(step);
      };
      this.truckRaf = window.requestAnimationFrame(step);
    },
    FitToStops() {
      const points = [...this.mappable, ...this.mappableCheckins];
      if (!points.length) return;
      // Re-fit ONLY when the coordinates actually changed. The truck is
      // deliberately not part of this: framing the map to a moving truck would
      // re-fit on every fix and fight whoever is watching it.
      const key = JSON.stringify(points.map((point) => [point.lat, point.lng]));
      if (key === this.fittedKey) return;
      this.center = {lat: points[0].lat, lng: points[0].lng};
      if (points.length < 2) {
        this.fittedKey = key;   // one point: centring IS the fit
        return;
      }
      // Map not mounted yet — leave the key unclaimed so the next call retries.
      if (!this.$refs.map || !this.$refs.map.$mapPromise) return;
      this.fittedKey = key;
      this.$refs.map.$mapPromise.then((map) => {
        if (!window.google || !window.google.maps) return;
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((point) => bounds.extend({lat: point.lat, lng: point.lng}));
        map.fitBounds(bounds, 40);
      }).catch(() => {
      });
    },
  },
  mounted() {
    this.FitToStops();
    this.RefreshRoadPath();
    this.RetargetTruck();
  },
  beforeDestroy() {
    // Leaving the page mid-glide would otherwise leave a rAF loop running
    // against a destroyed component.
    if (this.truckRaf) {
      window.cancelAnimationFrame(this.truckRaf);
      this.truckRaf = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.map-wrap {
  position: relative;

  .map-mode-btn {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 5;
    background: white;
    border: none;
    border-radius: 3px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .3);
    padding: 8px 14px;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;

    i {
      margin-right: 6px;
      color: #195BFF;
    }

    &:hover {
      background: #f4f6ff;
    }
  }
}

/* Google renders InfoWindow content outside the app's DOM, so the Lao font has
   to be named here — inherited faces do not reach it. */
.truck-info {
  font-family: $font;
  min-width: 170px;
  text-align: left;

  .ti-head {
    display: flex;
    align-items: center;
    gap: 6px;

    .ti-plate {
      font-weight: bold;
      font-size: 14px;
    }

    .ti-dir,
    .ti-live {
      font-size: 10px;
      border-radius: 10px;
      padding: 1px 7px;
      white-space: nowrap;
    }

    .ti-dir {
      &.dir-outbound {
        color: #195BFF;
        background: rgba(25, 91, 255, .1);
      }

      &.dir-return {
        color: #0a9;
        background: rgba(0, 170, 153, .12);
      }
    }

    .ti-live {
      color: white;
      background: #2dcc70;

      &.stale {
        background: #9aa0a6;
      }
    }
  }

  .ti-meta {
    font-size: 12px;
    color: #5a6472;
    margin: 2px 0 4px;
  }

  .ti-lines {
    display: flex;
    flex-wrap: wrap;
    gap: 3px 10px;
    font-size: 12px;
    color: #5a6472;

    b {
      font-weight: bold;
    }
  }

  .ti-note {
    margin-top: 3px;
    font-size: 11px;
    color: #e8a10f;
  }

  .ti-seen {
    margin-top: 4px;
    font-size: 11px;
    color: grey;
  }
}

.map-info {
  font-size: 13px;
  min-width: 150px;

  .map-info-title {
    font-weight: bold;
    margin-bottom: 6px;
  }

  .map-info-links {
    display: flex;
    gap: 12px;

    a {
      cursor: pointer;
      color: #195BFF;

      b {
        font-weight: bold;

        &.in {
          color: #2dcc70;
        }

        &.out {
          color: #e2513c;
        }
      }
    }
  }

  .map-info-offroute {
    margin-top: 6px;

    a {
      cursor: pointer;
      color: #e8a10f;

      b {
        font-weight: bold;
      }
    }
  }
}
</style>
